import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import { AuthContext } from "../Auth/AuthContext";
import i18n, { I18N_TENANT_DETECTION_ENABLED } from "../../i18n.js";
import { getSocket } from "../../services/socket-io";

const SettingsContext = createContext({ settings: [], getSetting: () => null });

const SettingsProvider = ({ children }) => {
  const { isAuth } = useContext(AuthContext);
  const [settings, setSettings] = useState([]);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const list = data?.settings || data || [];
      setSettings(Array.isArray(list) ? list : []);
      // Connect tenant language to i18n — only active when feature flag is enabled
      if (I18N_TENANT_DETECTION_ENABLED) {
        const langSetting = Array.isArray(list) ? list.find(s => s.key === "language") : null;
        const tenantLang = langSetting?.value || "es";
        i18n.changeLanguage(tenantLang);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (isAuth) fetchSettings();
    else setSettings([]);
  }, [isAuth, fetchSettings]);

  // Real-time: update context when admin changes a setting
  useEffect(() => {
    if (!isAuth) return;
    const socket = getSocket();
    const handler = (data) => {
      if (data.action === "update") {
        setSettings(prev => {
          const idx = prev.findIndex(s => s.key === data.setting.key);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...next[idx], value: data.setting.value };
            return next;
          }
          return [...prev, data.setting];
        });
      }
    };
    socket.on("settings", handler);
    return () => { socket.off("settings", handler); };
  }, [isAuth]);

  const getSetting = (key) => {
    const found = settings.find(s => s.key === key);
    return found ? found.value : null;
  };

  return (
    <SettingsContext.Provider value={{ settings, getSetting, reloadSettings: fetchSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export { SettingsContext, SettingsProvider };
